import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux'
import { Alert, Box, Grid, useTheme, Chip, Link, Typography } from "@mui/material";
import { Card, Pagination } from '../../ui';
import getFileIcon from "../../../helpers/getFileIcon";
import dayjs from 'dayjs'
import 'dayjs/locale/ru';
import duration from "dayjs/plugin/duration";
import { getProfileDocuments } from '../../../reducers/Profile';
import { isFailed, isLoading, isSuccess } from '../../../constants/redux.constants';
import { ProfileDocumentsSkeleton } from './skeletons';
import MakeAiReportModal from '../../ui/Modals/MakeAiReport';
dayjs.extend(duration);


const getDocumentStatusInfo = (document) => {
  const statusConfig = {
    0: {
      label: document.start_on_tz ? "Формируется" : "В очереди",
      color: "default"
    },
    1: {
      label: "Сформирован", 
      color: "success"
    },
    2: {
      label: "Обработан с ошибкой",
      color: "error"
    }
  };

  return statusConfig[document.status] || { label: "Неизвестно", color: "default" };
};


function DocumentsList() {
  document.title = "Д24 | Мои отчеты";
  const dispatch = useDispatch()
  const location = useLocation();
  const theme = useTheme()

  const { documentList, documentListLS } = useSelector(state => state.profile)

  const [searchParams, setSearchParams] = useSearchParams();

  const limit  = parseInt(searchParams.get("limit"))   || 50;
  const offset = parseInt(searchParams.get("offset"))  || 0;

  const [openedMakeAiReportId, setOpenedMakeAiReportId] = useState(null);

  useEffect(() => {
    dispatch(getProfileDocuments({ limit, offset }))
  }, [location.key])


  const handlePageChange = (newOffset) => {
    const params = { limit, offset: newOffset, tab: 'documents' };
    
    if (searchParams.has('ref')) {
      params.ref = searchParams.get('ref');
    }
   
    setSearchParams(params);
  }

  function formatTimeDifference(start_on_tz, finish_on_tz) {
    if (!start_on_tz || !finish_on_tz) {
      return null;
    }

    const diffMs = dayjs(finish_on_tz).diff(dayjs(start_on_tz));
    const diff   = dayjs.duration(diffMs);

    if (diffMs < 1000) {
      return "Менее секунды"
    }
  
    return `${diff.hours()}ч ${diff.minutes()}м ${diff.seconds()}с`;
  }

  return (
    <Box>
      <Grid sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', pb: 3 }}>
        <Typography sx={{ pl: { xs: 0.5, md: 0 } }} variant="h4">Мои отчеты</Typography>
        <Box sx={{ margin: { xs: '0 auto', md: '0' } }}>
          <Pagination
            count={documentList.length}
            limit={limit}
            offset={offset}
            loading={isLoading(documentListLS)}
            paginateCallback={(newOffset) => handlePageChange(newOffset)}
          /> 
        </Box>
      </Grid>

      {isLoading(documentListLS) && <ProfileDocumentsSkeleton />}

      {isFailed(documentListLS) && (
        <Box minWidth={'100%'} mt={2}>
          <Alert sx={{ p: 2.5 }} severity='error'>Произошла ошибка. Обновите страницу</Alert>
        </Box>
      )}

      {isSuccess(documentListLS) && !documentList.length && (
        <Box minWidth={'100%'}>
          <Alert sx={{ p: 2.5 }} severity='warning'>Нет созданных отчетов.</Alert>
        </Box>
      )}
  
      {isSuccess(documentListLS) && documentList.length > 0 && (
        <>
          <Grid container xs={12} gap={{ xs: 1, md: 2 }}>
            <Card sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: '100%',
              border: `1px solid ${theme.palette.OICO.stone[200]}`,
              p: { xs: 1, md: 1 },
              mb: { xs: 1, md: 2 }
            }}
            >
              {
                documentList.map((document, index) => {

                  const fullName = `${document.file_name}.${document.file_format}`;
                  const canMakeAIReport = ['xlsx', 'csv'].includes(document.file_format) && document.status === '1' && dayjs().diff(dayjs(document.finish_on_tz), 'days') <= 3;
                  const statusInfo = getDocumentStatusInfo(document);
                  const timeDiff = formatTimeDifference(document.start_on_tz, document.finish_on_tz);
                  const createdDate = document.created_on_tz ? dayjs(document.created_on_tz).locale('ru').format('DD MMMM YYYY HH:mm') : 'Не указано';

                  const FileContent = (
                    <>
                      {getFileIcon(fullName)}
                      <Typography variant="h6" sx={{ ml: 2, mb: 0, fontWeight: 500 }}>
                        {fullName}
                      </Typography>
                    </>
                  );

                  const AiReportButton = (
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.link', cursor: 'pointer', mt: '2px', '&:hover': { textDecoration: 'underline' } }} onClick={() => setOpenedMakeAiReportId(document.report_id)}>
                      Создать ИИ-отчет
                    </Typography>
                  );

                  return (
                    <Box
                      key={document.report_id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderBottom: index < documentList.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        p: { xs: 1.5, md: 2 },
                      }}
                    >

                      <Typography sx={{ fontSize: '0.7em', color: theme.palette.grey.main }}>
                        #{document.report_id} | {createdDate}
                      </Typography>

                      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} sx={{ alignItems: { xs: 'stretch', md: 'center' }, mt: 0.75 }}>

                        <Box sx={{ width: { xs: '100%', md: '50%' } }}>

                          {
                            document.file_uri ?
                              <Link href={document.file_uri} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.link, width: 'fit-content' }}>
                                {FileContent}
                              </Link>
                              :
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {FileContent}
                              </Box>
                          }

                          <Box sx={{ display: { xs: 'block', md: 'none' } }}>

                            {
                              canMakeAIReport &&
                              <Typography sx={{ fontSize: '0.9rem', mt: 1.5 }}>
                                {timeDiff}
                              </Typography>
                            }

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>

                              {
                                canMakeAIReport ? AiReportButton : <Typography sx={{ fontSize: '0.9rem' }}>{timeDiff}</Typography>
                              }

                              <Chip label={statusInfo.label} color={statusInfo.color} size="small" />

                            </Box>

                          </Box>

                        </Box>

                        <Box sx={{ flex: 1, mt: '2px', display: { xs: 'none', md: 'block' } }}>
                          <Typography sx={{ fontSize: '0.9rem' }}>{timeDiff}</Typography>
                        </Box>

                        <Box sx={{ width: { xs: 'auto', md: '33.333%' }, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>

                          {canMakeAIReport && AiReportButton}
                          <Chip label={statusInfo.label} color={statusInfo.color} size="small" />

                        </Box>

                      </Box>

                    </Box>
                  );

                })
              }
            </Card>
          </Grid>
        </>
      )} 

      <MakeAiReportModal openedReportId={openedMakeAiReportId} report_uri={documentList.find(document => document.report_id === openedMakeAiReportId)?.file_uri} setReportId={setOpenedMakeAiReportId} />

    </Box>
    
  )
}

export default DocumentsList;